import { t } from "@lingui/macro";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alerts from "@/client/components/alerts";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useAppContext } from "@/client/hooks/context";
import { useUser } from "@/client/services/user";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateString: string | null) => {
  if (!dateString) return "--";
  try {
    return format(new Date(dateString), "MMM dd, yyyy hh:mm a");
  } catch {
    return "--";
  }
};

const encodeParamsAsBase64 = (selfCodingId: string, studentId: string): string => {
  const data = { selfCodingId, studentId, type: "self-coding" };
  return btoa(JSON.stringify(data));
};

// ─── Styled Components ───────────────────────────────────────────────────────

const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2),
  backgroundColor: "#f7faff",
  borderRadius: "12px",
  border: "1px solid #dde8fb",
  marginBottom: theme.spacing(1.5),
}));

const filterSelectSx = {
  fontSize: "13px",
  bgcolor: "#fff",
  borderRadius: "8px",
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: "1px solid #eaf0fb",
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "64vh",
  overflowY: "auto",
}));

const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: "#f7faff",
    fontWeight: 700,
    fontSize: "13px",
    color: "#374151",
    borderBottom: "2px solid #dde8fb",
    whiteSpace: "nowrap",
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#f0f6ff" },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #eaf0fb",
    padding: "10px 14px",
  },
}));

// ─── Status Chip ─────────────────────────────────────────────────────────────

const StatusChip = ({ status }: { status: string }) => {
  switch (status.toLowerCase()) {
    case "draft":
      return (
        <ChipLabel
          label={t`Draft`}
          sx={{ color: "grey.main", backgroundColor: "grey.light", fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    case "published":
      return (
        <ChipLabel
          label={t`Published`}
          sx={{ color: "blue.main", backgroundColor: "blue.light", fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    case "completed":
      return (
        <ChipLabel
          label={t`Completed`}
          sx={{ color: "green.main", backgroundColor: "green.light", fontSize: "11px", p: "0px", height: "24px" }}
        />
      );
    default:
      return null;
  }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ─── Main Component ──────────────────────────────────────────────────────────

const SelfCoding = () => {
  const [data, setData] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCodingId, setSelectedCodingId] = useState<string>("");
  const { user } = useUser();
  const { loading, setLoading, reloading, setReloading } = useAppContext();
  const navigate = useNavigate();
  const studentId = user?.id;

  // ─── Filters ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  // ─── Pagination ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [alert, setAlert] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: "",
    severity: "info",
  });

  // ─── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/integration/external-service/self-coding`, {
          params: { studentId, page: 1, limit: 10000 },
        });
        setData(res.data.result.data || res.data.result || []);
      } catch (error) {
        console.error("Error fetching self-coding:", error);
        setAlert({ open: true, message: t`Failed to fetch self-coding`, severity: "error" });
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    if (studentId) fetchData();
  }, [reloading, studentId, setLoading]);

  // ─── Reset page on filter change ──────────────────────────────────────────
  useEffect(() => {
    setPage(0);
  }, [search, filterCategory, filterStatus]);

  const handleCloseAlert = () => setAlert({ open: false, message: "", severity: "info" });

  const handleTake = (id: string) => {
    const selfCoding = data.find((item: any) => item._id === id);
    if (selfCoding) {
      const encodedData = encodeParamsAsBase64(id, studentId || "");
      window.location.href = `${import.meta.env.VITE_INTERVIEW_WEB_APP_URL}/self-coding?data=${encodedData}`;
    }
  };

  const handleViewResult = (id: string) => {
    const selfCoding = data.find((item: any) => item._id === id);
    if (selfCoding) {
      const encodedData = encodeParamsAsBase64(id, studentId || "");
      window.open(
        `${import.meta.env.VITE_INTERVIEW_WEB_APP_URL}/self-coding/result?data=${encodedData}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/integration/external-service/self-coding/${selectedCodingId}`, {
        params: { studentId },
      });
      setAlert({ open: true, message: t`Self-coding deleted successfully`, severity: "success" });
      setReloading(reloading + 1);
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.response?.data?.error || t`Failed to delete self-coding`,
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCodingId("");
    }
  };

  // ─── Rows — sorted latest created_at first ────────────────────────────────
  const rows = useMemo(
    () =>
      [...data]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((item: any) => ({
          id: item._id,
          name: item.name,
          category: item.category ?? "--",
          rawCategory: item.category ?? "",
          questionCount: item.questionCount || 0,
          score:
            item.score !== null && item.score !== undefined
              ? `${item.score}/${item.totalScore}`
              : "--",
          status: item.status,
          completedAt: item.completedAt,
          createdAt: item.createdAt,
        })),
    [data]
  );

  // ─── Filter options ───────────────────────────────────────────────────────
  const categoryOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.category)))],
    [rows]
  );
  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.status)))],
    [rows]
  );

  // ─── Filtered rows ────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const query = search.toLowerCase().trim();
    return rows.filter((row) => {
      if (
        query &&
        !row.name.toLowerCase().includes(query) &&
        !row.category.toLowerCase().includes(query)
      )
        return false;
      if (filterCategory !== "All" && row.category !== filterCategory) return false;
      if (filterStatus !== "All" && row.status !== filterStatus) return false;
      return true;
    });
  }, [rows, search, filterCategory, filterStatus]);

  // ─── Paginated rows ───────────────────────────────────────────────────────
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const hasActiveFilters = search !== "" || filterCategory !== "All" || filterStatus !== "All";

  const clearFilters = () => {
    setSearch("");
    setFilterCategory("All");
    setFilterStatus("All");
  };

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: "15px",
            fontWeight: 700,
            color: "primary.main",
            mb: "-2px",
            userSelect: "none",
          }}
        >
          {t`Self-Coding`}
        </Box>
        <Box sx={{ pb: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/self-coding/create")}
            sx={{ textTransform: "none", fontSize: "13px", height: "32px" }}
          >
            {t`Create Self Coding`}
          </Button>
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar sx={{ flexWrap: "nowrap", alignItems: "center", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "primary.main",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {t`Filters`}
        </Typography>

        {/* Search */}
        <TextField
          size="small"
          placeholder={t`Search name, category...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px",
            minWidth: 120,
            maxWidth: 200,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
              "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        {/* Category Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Category`}</InputLabel>
          <Select
            label={t`Category`}
            value={filterCategory}
            onChange={(e: SelectChangeEvent) => setFilterCategory(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Status`}</InputLabel>
          <Select
            label={t`Status`}
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px", textTransform: "capitalize" }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Count + Clear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasActiveFilters
                ? `${filteredRows.length} of ${rows.length} codings`
                : `${rows.length} codings`}
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              onClick={clearFilters}
              sx={{
                fontSize: "11px",
                bgcolor: "#fff0f0",
                color: "#c62828",
                borderColor: "#ffcdd2",
                "& .MuiChip-deleteIcon": { color: "#c62828" },
              }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {t`Loading self-codings...`}
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasActiveFilters
              ? t`No codings match your filters. Try adjusting or clearing them.`
              : t`No self-codings found.`}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #eaf0fb",
            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "180px" }}>{t`Name`}</TableCell>
                  <TableCell sx={{ width: "120px" }}>{t`Category`}</TableCell>
                  <TableCell align="center" sx={{ width: "90px" }}>{t`Questions`}</TableCell>
                  <TableCell align="center" sx={{ width: "90px" }}>{t`Score`}</TableCell>
                  <TableCell align="center" sx={{ width: "160px" }}>{t`Created At`}</TableCell>
                  <TableCell align="center" sx={{ width: "160px" }}>{t`Completed At`}</TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>{t`Status`}</TableCell>
                  <TableCell align="center" sx={{ width: "130px" }}>{t`Actions`}</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const isDraft = row.status === "draft";
                  const isPublished = row.status === "published";
                  const isCompleted = row.status === "completed";

                  return (
                    <StyledTableRow key={row.id}>
                      {/* Name */}
                      <TableCell sx={{ width: "180px", overflow: "hidden" }}>
                        <Tooltip title={row.name} placement="top">
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "primary.main",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Category */}
                      <TableCell sx={{ width: "120px", overflow: "hidden" }}>
                        <Chip label={row.category} size="small"
                        sx={{ fontSize: "11px", width: "72px", minWidth: "72px", justifyContent: "center",
                          bgcolor: row.rawCategory === "practice" ? "#f0fff4" : "#fff8e1",
                          color: row.rawCategory === "practice" ? "#388e3c" : "#f57f17" }}
                        variant="outlined" />
                      </TableCell>

                      {/* Questions */}
                      <TableCell align="center" sx={{ width: "90px" }}>
                        <Typography sx={{ fontSize: "13px" }}>{row.questionCount}</Typography>
                      </TableCell>

                      {/* Score */}
                      <TableCell align="center" sx={{ width: "90px" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: row.score !== "--" ? 600 : 400 }}>
                          {row.score}
                        </Typography>
                      </TableCell>

                      {/* Created At */}
                      <TableCell align="center" sx={{ width: "160px" }}>
                        <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                          {formatDate(row.createdAt)}
                        </Typography>
                      </TableCell>

                      {/* Completed At */}
                      <TableCell align="center" sx={{ width: "160px" }}>
                        <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                          {formatDate(row.completedAt)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell align="center" sx={{ width: "100px" }}>
                        <StatusChip status={row.status} />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" sx={{ width: "130px" }}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {/* Edit */}
                          <Tooltip title={isDraft ? t`Edit` : t`Edit (Not Available)`}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/self-coding/edit/${row.id}`)}
                                disabled={!isDraft}
                              >
                                <EditNoteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* Start / Not Available */}
                          {isPublished && (
                            <Tooltip title={t`Start Coding`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleTake(row.id)}
                              >
                                <ArrowForwardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isCompleted && (
                            <Tooltip title={t`View Result`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewResult(row.id)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isDraft && (
                            <Tooltip title={t`Not Available`}>
                              <span>
                                <IconButton size="small" disabled>
                                  <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Delete */}
                          <Tooltip title={isDraft ? t`Delete` : t`Delete (Not Available)`}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedCodingId(row.id);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={!isDraft}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #eaf0fb",
              bgcolor: "#f7faff",
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
              },
            }}
          />
        </Paper>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle
          sx={{
            backgroundColor: "primary.dark",
            marginBottom: "5px",
            padding: "5px",
            paddingBottom: "3px",
            paddingTop: "2px",
            color: "white",
            textAlign: "start",
            fontSize: "14px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <WarningRoundedIcon sx={{ mr: "3px", pb: "6px", fontSize: "28px", color: "#eb4934" }} />
            {t`Delete Confirmation`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 1, mt: 1, width: "360px" }}>
          <DialogContentText sx={{ pb: 2 }}>
            {t`Are you sure you want to delete this self coding?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: "10px" }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ textTransform: "none", border: "none" }}
          >
            {t`Cancel`}
          </Button>
          <Button
            onClick={handleDelete}
            color="primary"
            size="small"
            variant="contained"
            autoFocus
            sx={{ textTransform: "none", mb: 1 }}
          >
            {t`Delete`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelfCoding;